import { useMemo } from 'react';
import styled from '@emotion/styled';

import { BreakpointSizes, mq } from '@taleemabad/dsm/themes';
import {
  Badge,
  Button,
  Card,
  Divider,
  Spacing,
  StyledDiv,
  StyledFixedButtonWrapper,
  Typography,
} from '@taleemabad/dsm/components';
import { VisitStatus } from '@taleemabad/db';

export interface TeacherVisit {
  id: string;
  teacherName: string;
  status: VisitStatus;
  date?: string;
  comments?: string | null;
  coachName?: string;
}

export interface RmCoachVisitDetailsProps {
  schoolName: string;
  emis: string;
  teacherVisits: TeacherVisit[];
  onDone: () => void;
  coachName?: string;
  onAssignCoach?: () => void;
}

export const RmCoachVisitDetails = ({
  schoolName,
  emis,
  teacherVisits,
  onDone,
  coachName,
  onAssignCoach,
}: RmCoachVisitDetailsProps) => {
  const completedCount = useMemo(
    () =>
      teacherVisits.filter((t) => t.status === VisitStatus.Completed).length,
    [teacherVisits],
  );

  return (
    <StyledDiv
      flexDirection="column"
      width="100%"
      padding="0 0 var(--rk-spacing-xxxxxxxxxxl) 0"
    >
      <Spacing marginTop="xl" marginBottom="xl">
        <StyledDiv flexDirection="column">
          <Typography variant="textBodyLarge">School</Typography>

          <Typography variant="textHeadlineMedium">{emis}</Typography>
          <StyledCapitalizedTypography variant="textHeadlineMedium">
            {schoolName}
          </StyledCapitalizedTypography>

          <Spacing marginTop="xxxl" />
          <Divider />
          <Spacing marginBottom="xl" />

          <StyledDiv alignItem="center" justifyContent="space-between">
            <StyledDiv flexDirection="column">
              <Typography variant="textTitleMedium">
                {coachName &&
                coachName !== 'No Coaches Assigned' &&
                coachName.split(',').length === 1
                  ? 'Coach Assigned:'
                  : 'Coaches Assigned:'}
              </Typography>
              <Spacing marginTop="sm" />
              {coachName && coachName !== 'No Coaches Assigned' ? (
                <StyledDiv flexDirection="column" gap={2}>
                  {coachName.split(',').map((coach, index) => (
                    <StyledCapitalizedTypography
                      key={index}
                      variant="textBodyMedium"
                    >
                      • {coach.trim()}
                    </StyledCapitalizedTypography>
                  ))}
                </StyledDiv>
              ) : (
                <Typography variant="textBodyMedium">-</Typography>
              )}
            </StyledDiv>
            {onAssignCoach && (
              <Button variant="outlined" onClick={onAssignCoach}>
                {coachName && coachName !== 'No Coaches Assigned'
                  ? coachName.split(',').length === 1
                    ? 'Change Coach'
                    : 'Change Coaches'
                  : 'Assign Coaches'}
              </Button>
            )}
          </StyledDiv>

          <Spacing marginBottom="xl" />
          <Divider />

          <Spacing marginTop="xxxl" marginBottom="lg">
            <StyledDiv justifyContent="flex-end" gap={4}>
              <Typography variant="textBodyMedium">
                Observations Conducted:
              </Typography>
              <Typography variant="textBodyMedium">
                {completedCount}/{teacherVisits.length}
              </Typography>
            </StyledDiv>
          </Spacing>

          <StyledCardsContainer>
            {teacherVisits.length === 0 ? (
              <>
                <Spacing marginTop="xxxxl" />
                <Typography variant="textBodyMedium" textAlign="center">
                  No teachers available.
                </Typography>
              </>
            ) : (
              teacherVisits.map((visit) => {
                const isComplete = visit.status === VisitStatus.Completed;
                return (
                  <Card variant="outline" minHeight="60" key={visit.id}>
                    <Typography variant="textLabelLarge">Teacher</Typography>
                    <StyledCapitalizedTypography variant="textTitleMedium">
                      {visit.teacherName}
                    </StyledCapitalizedTypography>
                    <Spacing marginBottom="sm" />
                    <StyledDiv gap={8}>
                      <StyledDiv flexDirection="column">
                        <Typography variant="textTitleSmall">
                          Conducted On:
                        </Typography>
                        <Typography variant="textTitleSmall">
                          Planned By:
                        </Typography>
                      </StyledDiv>
                      <Spacing marginBottom="xs" />
                      <StyledDiv flexDirection="column">
                        <Typography variant="textTitleSmall">
                          {isComplete && visit.date ? visit.date : '-'}
                        </Typography>
                        <StyledCapitalizedTypography variant="textTitleSmall">
                          {visit.coachName || '-'}
                        </StyledCapitalizedTypography>
                      </StyledDiv>
                    </StyledDiv>
                    <Spacing marginBottom="md" />
                    <Badge
                      size="small"
                      label={isComplete ? 'Completed' : 'Incomplete'}
                      color={
                        isComplete
                          ? 'var(--md-sys-color-on-success)'
                          : 'var(--md-sys-color-error)'
                      }
                      type="filled"
                    />
                    <Spacing marginBottom="lg" />
                    <StyledCommentText>
                      <Typography variant="textBodyMedium">
                        {visit.comments && visit.comments.trim()
                          ? visit.comments
                          : 'No comments'}
                      </Typography>
                    </StyledCommentText>
                  </Card>
                );
              })
            )}
          </StyledCardsContainer>

          <StyledFixedButtonWrapper>
            <Button variant="filled" onClick={onDone}>
              Done
            </Button>
          </StyledFixedButtonWrapper>
        </StyledDiv>
      </Spacing>
    </StyledDiv>
  );
};

const StyledCapitalizedTypography = styled(Typography)`
  text-transform: capitalize;
`;

const StyledCommentText = styled.div`
  background: var(--md-sys-color-surface-container-low);
  border-radius: var(--rk-spacing-md);
  padding: var(--rk-spacing-lg);
  margin-top: var(--rk-spacing-md);
  margin-bottom: var(--rk-spacing-md);
  width: 100%;
  box-sizing: border-box;
`;

const StyledCardsContainer = styled.div`
  width: 100%;
  display: grid;
  grid-template-columns: 1fr;
  gap: var(--rk-spacing-md);

  ${mq(BreakpointSizes.desktop)} {
    grid-template-columns: repeat(3, 1fr);
    gap: 24px;
  }
`;
